/** getMessage 单条：VIDEO_PATH 可为单个 URL 或 | 分隔（V1/V2 兼容） */
export function parseVideoPaths(videoList) {
  if (videoList == null || videoList === '') {
    return [];
  }
  if (typeof videoList !== 'string') {
    return [];
  }
  const trimmed = videoList.trim();
  if (!trimmed) {
    return [];
  }
  if (trimmed.includes('|')) {
    return trimmed
      .split('|')
      .map(s => s.trim())
      .filter(Boolean);
  }
  return [trimmed];
}

export function parseVideoDurations(videoDurations) {
  if (!videoDurations) {
    return [];
  }
  try {
    const parsed = JSON.parse(videoDurations);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

export function normalizeScreenPicList(screenPic) {
  if (!Array.isArray(screenPic)) {
    return [];
  }
  return screenPic.map(item => String(item || '').trim()).filter(Boolean);
}

/**
 * V2：PIC_AND_VIDEO_SEQUENCE 为 [{ type, index, value }]，value 为完整 URL
 * V1：为文件名片段数组，需与 SCREEN_PIC / VIDEO_PATH 匹配
 */
export function isV2PicAndVideoSequence(sequence) {
  if (!Array.isArray(sequence) || !sequence.length) {
    return false;
  }
  const first = sequence[0];
  return (
    first &&
    typeof first === 'object' &&
    (first.type === 'image' || first.type === 'video') &&
    typeof first.value === 'string' &&
    first.value.length > 0
  );
}

export function buildFormatVideoList({
  videoList,
  videoDurations,
  videoPreviewPic
}) {
  const videoPaths = parseVideoPaths(videoList);
  if (!videoPaths.length) {
    return [];
  }
  const durations = parseVideoDurations(videoDurations);
  const previews = Array.isArray(videoPreviewPic) ? videoPreviewPic : [];

  return videoPaths
    .map((videoPath, index) => {
      const path = String(videoPath || '').trim();
      if (!path) {
        return null;
      }
      const url = String(previews[index] || '').trim();
      return {
        url,
        videoPath: path,
        duration: durations[index] || '0:00'
      };
    })
    .filter(Boolean);
}

export function buildMessageMediaSequence({
  screenPic,
  videoList,
  videoDurations,
  videoPreviewPic,
  picAndVideoSequence
}) {
  const formatImgList = normalizeScreenPicList(screenPic).map(url => ({ url }));
  const formatVideoList = buildFormatVideoList({
    videoList,
    videoDurations,
    videoPreviewPic
  });

  if (!picAndVideoSequence) {
    return [
      ...formatImgList.map(item => ({ type: 'image', url: item.url })),
      ...formatVideoList.map(item => ({
        type: 'video',
        url: item.url,
        videoPath: item.videoPath,
        duration: item.duration
      }))
    ];
  }

  let parsed;
  try {
    parsed = JSON.parse(picAndVideoSequence);
  } catch (e) {
    return [
      ...formatImgList.map(item => ({ type: 'image', url: item.url })),
      ...formatVideoList.map(item => ({
        type: 'video',
        url: item.url,
        videoPath: item.videoPath,
        duration: item.duration
      }))
    ];
  }

  if (!parsed?.length) {
    return formatImgList.map(item => ({ type: 'image', url: item.url }));
  }

  if (isV2PicAndVideoSequence(parsed)) {
    return parsed
      .map(entry => {
        const idx = Number(entry.index) || 0;
        if (entry.type === 'video') {
          const fromList = formatVideoList[idx];
          const videoPath =
            String(entry.value || '').trim() ||
            (fromList && fromList.videoPath) ||
            '';
          const url =
            (fromList && fromList.url) ||
            String((videoPreviewPic && videoPreviewPic[idx]) || '').trim();
          if (!url && !videoPath) {
            return null;
          }
          return {
            type: 'video',
            url: url || videoPath,
            videoPath,
            duration: (fromList && fromList.duration) || '0:00'
          };
        }
        const imageUrl = String(entry.value || '').trim();
        if (!imageUrl) {
          return null;
        }
        return {
          type: 'image',
          url: imageUrl
        };
      })
      .filter(Boolean);
  }

  // V1：sequence 项为文件名片段
  const mergeList = [...formatImgList, ...formatVideoList];
  const sequence = [];
  parsed.forEach(fragment => {
    const key = typeof fragment === 'string' ? fragment : String(fragment);
    const mergeListItem = mergeList.find(cItem => {
      if (cItem.videoPath) {
        return cItem.videoPath.includes(key);
      }
      return cItem.url && cItem.url.includes(key);
    });
    if (mergeListItem) {
      sequence.push({
        type: mergeListItem.videoPath ? 'video' : 'image',
        url: mergeListItem.url,
        videoPath: mergeListItem.videoPath,
        duration: mergeListItem.duration || '0:00'
      });
    }
  });
  if (sequence.length) {
    return sequence;
  }

  return [
    ...formatImgList.map(item => ({ type: 'image', url: item.url })),
    ...formatVideoList.map(item => ({
      type: 'video',
      url: item.url,
      videoPath: item.videoPath,
      duration: item.duration
    }))
  ];
}
